import { Fragment, useLayoutEffect } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { canBackupAdmin, canUserPerms } from '../../lib/permissions';
import { PresenceStack } from '../presence/PresenceStack';
import { FeedBell } from '../feed/FeedBell';
import { RIGHT_CONTROLS, RIGHT_PRIORITY, anyExpanded } from './topbarControls';
import { TopbarOverflowMenu } from './TopbarOverflowMenu';

/**
 * The right-hand group of the top bar.
 *
 * The topbar is the largest region in the port, so it moved in three parts rather than one:
 * this group first (mostly toggles), then the left group, then the search. Splitting was safe
 * because the fit mechanism measures the ZONES rather than caring who rendered them. What it
 * DOES care about is being re-run whenever a button appears or disappears, so this component
 * asks for a re-measure in a layout effect, before the browser paints.
 *
 * THE CONTROLS RENDER FROM THE REGISTRY (`topbarControls.tsx`, plan 1.3): same markup, same
 * classes, same ids — the extraction was verified against the pixel baselines as a pure
 * refactor. The registry is what lets each control live in exactly one of two places: inline
 * here, or as a MenuItem in the "…" overflow menu.
 *
 * PRESENCE STACK. This used to be an empty div for the vanilla `updatePresenceStack()` to
 * fill — an escape hatch with an expiry date, because that function wrote into every
 * `.presence-stack` in the page at once. The date has passed: it is a real component now,
 * reading the roster across the bridge like everything else.
 */

export function TopbarRight({ host }: { host: HTMLElement }) {
  const s = useAppState();

  const treeOpen = anyExpanded(s);
  // Either permission draws the button (V0.48); it is in this effect's dependency list so
  // the top bar re-measures when a role change flips it, exactly as before.
  const showPerms = canUserPerms(s.perm) || canBackupAdmin(s.perm);
  const showLangs = s.languages.length > 1;
  const hasLinks = s.links.length > 0;
  const userLabel = s.currentUser ? s.currentUser.displayName || s.currentUser.username : '';

  // Any of the above changing resizes this zone, which is exactly what decides whether the
  // search box still fits. Layout effect, not effect: the measurement has to happen before
  // paint or the search box visibly flickers on sign-in.
  //
  // Gated on `booted` because this component mounts before boot() has answered anything,
  // which is BEFORE boot() finishes and registers the actions — so an ungated first pass
  // asked for a function that did not exist yet and logged a bridge error on every load.
  // `booted` flips in the same breath as the registration, so it is the honest condition.
  // `s.feedUnread` is here because the bell's badge appearing, disappearing or gaining a
  // digit changes this zone's width, and nothing else would re-measure for it (plan Phase 3).
  useLayoutEffect(() => {
    if (!s.booted) return;
    callAction('fitTopbar');
  }, [s.booted, showPerms, showLangs, hasLinks, userLabel, treeOpen, s.showLinks, s.feedUnread]);

  // The host IS `.tb-right`; the stylesheet lays its children out. Nothing to toggle here.
  useLayoutEffect(() => {
    host.classList.add('tb-right');
  }, [host]);

  // A control is inline OR in the "…" menu, never both (plan 1.3). The menu lists its side's
  // overflowed controls in first-out priority order — the order the plan names them in.
  const ovf = new Set(s.topbarOverflow);
  const menuControls = RIGHT_PRIORITY.map((id) => RIGHT_CONTROLS.find((c) => c.id === id)!).filter(
    (c) => ovf.has(c.id) && c.visible(s),
  );

  return (
    <>
      {RIGHT_CONTROLS.map((c) =>
        c.visible(s) && !ovf.has(c.id) ? <Fragment key={c.id}>{c.inline(s)}</Fragment> : null,
      )}

      <PresenceStack id="presence-stack" />

      {/* The bell (plan Phase 3): a NEVER-overflow fixture, placed the way presence is —
          not in the registry, so the fit controller can never pull it into a "…" menu. */}
      <FeedBell />

      <span className="user-chip" id="user-chip" title="Signed-in user">
        {userLabel}
      </span>

      {/* Immediately left of logout (user decision): the one door that never moves stays
          in the corner, and everything that had to leave the bar sits right beside it. */}
      <TopbarOverflowMenu side="right" controls={menuControls} />

      <button className="btn" id="btn-logout" title="Log out" onClick={() => callAction('logout')}>
        <svg className="ico">
          <use href="#i-logout" />
        </svg>
      </button>
    </>
  );
}
